import { useEffect, useId, useState } from "react";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
    getSkillEditorFilenameValidationIssues,
    getSkillEditorPublishedFilenameComparisonKey,
    type SkillEditorSaveReconcileRecovery,
} from "../../skill-editor-save";

export type SaveRecoveryDialogCase = SkillEditorSaveReconcileRecovery;

export interface SaveRecoveryDialogProps {
    recovery: SaveRecoveryDialogCase | null;
    siblingNames: readonly string[];
    isResolving?: boolean;
    onRecreateUnderSameName: (clientKey: string) => void;
    onRenameAndRecreate: (clientKey: string, name: string) => void;
    onDiscardLocal: (clientKey: string) => void;
    onReloadServerFile: (clientKey: string) => void;
    onKeepLocalAndRename: (clientKey: string, name: string) => void;
}

const signatureOf = (recovery: SaveRecoveryDialogCase) =>
    recovery.kind === "idless-local"
        ? `${recovery.kind}:${recovery.clientKey}:${recovery.fileName}`
        : `${recovery.kind}:${recovery.clientKey}:${recovery.remoteServerId}:${recovery.remoteUpdatedAt}`;

const describeNameProblem = (
    name: string,
    remoteName: string | null,
    siblingNames: readonly string[]
): string | null => {
    const filenameIssues = getSkillEditorFilenameValidationIssues(name);
    const filenameIssue =
        filenameIssues.find(({ code }) => code === "published-stem-empty") ??
        filenameIssues[0];
    if (filenameIssue) return filenameIssue.message;
    if (
        remoteName !== null &&
        getSkillEditorPublishedFilenameComparisonKey(name) ===
            getSkillEditorPublishedFilenameComparisonKey(remoteName)
    ) {
        return `"${remoteName}" is the name already on the server`;
    }
    const sibling = siblingNames.find(
        (candidate) =>
            getSkillEditorPublishedFilenameComparisonKey(candidate) ===
            getSkillEditorPublishedFilenameComparisonKey(name)
    );
    if (sibling !== undefined) {
        return `"${sibling}" is already used by another reference`;
    }
    return null;
};

export function SaveRecoveryDialog({
    recovery,
    siblingNames,
    isResolving = false,
    onRecreateUnderSameName,
    onRenameAndRecreate,
    onDiscardLocal,
    onReloadServerFile,
    onKeepLocalAndRename,
}: SaveRecoveryDialogProps) {
    const nameFieldId = useId();
    const nameErrorId = useId();
    const [draftName, setDraftName] = useState("");
    const [isRenaming, setIsRenaming] = useState(false);
    const signature = recovery ? signatureOf(recovery) : null;

    useEffect(() => {
        setDraftName("");
        setIsRenaming(false);
    }, [signature]);

    if (!recovery) return null;

    const isIdlessLocal = recovery.kind === "idless-local";
    const localFileName = isIdlessLocal
        ? recovery.fileName
        : recovery.localFileName;
    const trimmedName = draftName.trim();
    const nameProblem = trimmedName
        ? describeNameProblem(
              trimmedName,
              isIdlessLocal ? null : recovery.remoteFileName,
              siblingNames
          )
        : null;
    const canSubmitName = trimmedName !== "" && nameProblem === null;
    const renameLabel = isIdlessLocal
        ? "Rename and recreate"
        : "Keep local and rename";

    return (
        <Dialog open onOpenChange={() => undefined}>
            <DialogContent className="sm:max-w-md" showCloseButton={false}>
                <DialogHeader>
                    <DialogTitle>
                        {isIdlessLocal
                            ? "Recover unsaved reference"
                            : "Resolve reference name collision"}
                    </DialogTitle>
                    <DialogDescription>
                        {isIdlessLocal
                            ? `"${localFileName}" is no longer on the server. Your edits to it are kept here.`
                            : `"${localFileName}" collides with "${recovery.remoteFileName}" already on the server at ${recovery.publishedPath}. Reloading discards your local version; renaming keeps it as a separate reference.`}
                    </DialogDescription>
                </DialogHeader>

                {isRenaming && (
                    <div className="space-y-1.5">
                        <Label htmlFor={nameFieldId}>New reference name</Label>
                        <Input
                            id={nameFieldId}
                            value={draftName}
                            autoFocus
                            aria-invalid={nameProblem !== null}
                            aria-describedby={
                                nameProblem ? nameErrorId : undefined
                            }
                            onChange={(event) =>
                                setDraftName(event.target.value)
                            }
                            disabled={isResolving}
                        />
                        {nameProblem && (
                            <p
                                id={nameErrorId}
                                className="text-destructive text-sm"
                            >
                                {nameProblem}
                            </p>
                        )}
                    </div>
                )}

                <DialogFooter>
                    {isRenaming ? (
                        <>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsRenaming(false)}
                                disabled={isResolving}
                            >
                                Cancel rename
                            </Button>
                            <Button
                                type="button"
                                onClick={() =>
                                    isIdlessLocal
                                        ? onRenameAndRecreate(
                                              recovery.clientKey,
                                              trimmedName
                                          )
                                        : onKeepLocalAndRename(
                                              recovery.clientKey,
                                              trimmedName
                                          )
                                }
                                disabled={isResolving || !canSubmitName}
                            >
                                {renameLabel}
                            </Button>
                        </>
                    ) : (
                        <>
                            {isIdlessLocal && (
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() =>
                                        onRecreateUnderSameName(
                                            recovery.clientKey
                                        )
                                    }
                                    disabled={isResolving}
                                >
                                    Recreate under the same name
                                </Button>
                            )}
                            {isIdlessLocal ? (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() =>
                                        onDiscardLocal(recovery.clientKey)
                                    }
                                    disabled={isResolving}
                                >
                                    Discard this reference
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    variant="destructive"
                                    onClick={() =>
                                        onReloadServerFile(recovery.clientKey)
                                    }
                                    disabled={isResolving}
                                >
                                    Discard local and reload server file
                                </Button>
                            )}
                            <Button
                                type="button"
                                onClick={() => setIsRenaming(true)}
                                disabled={isResolving}
                            >
                                {renameLabel}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
